import { useEffect, useState } from 'react';
import type { Nullable, StringMap } from 'corgi-types';

import { RenderingType } from '../../../../product-mvc-buttons/components/enums';
import type { Group, Variation } from '../../../../product-mvc-buttons/components/types';
import { getSelectedValues } from '../../../../product-mvc-buttons/components/utils';
import { useGQLLocale } from '../../../hooks/locale';
import { useLocalization } from '../../../providers/LocaleProvider';
import type { ErrorMessage } from '../../../types/error';
import { fetchVariations } from '../gql/fetchVariations';
import { useDrawer, useSelectedValueImages } from '../hooks';

const getGroupValuesCount = (groups: Group[]) =>
  groups.map(group => group.values.length).reduce((sum, length) => sum + length, 0);

const getErrorMessage = (message: string) => ({
  message,
  translated: true,
  timestamp: Date.now(),
});

export const useDrawerProps = (sku: string) => {
  const [variation, setVariation] = useState<Variation | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Nullable<ErrorMessage>>(null);
  const [selectedGroupValues, setSelectedGroupValues] = useState<StringMap>({});
  const [count, setCount] = useState(0);

  const locale = useGQLLocale();
  const { t } = useLocalization();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const variations = await fetchVariations({
          sku,
          locale,
        });

        if (variations.length > 0) {
          const selectedGroupValuesData = getSelectedValues(variations);
          const [filteredVariation] = variations.filter(
            variationValue => variationValue.renderingType === RenderingType.Icon
          );
          const countData = getGroupValuesCount(filteredVariation.groups);

          setVariation(filteredVariation);
          setSelectedGroupValues(selectedGroupValuesData);
          setCount(countData);
        }
      } catch {
        setError(getErrorMessage(t('h24_something_went_wrong')));
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, []);

  const { selectedValue, form } = useDrawer(variation?.groups || [], selectedGroupValues, count);
  const { images, fabricSample } = useSelectedValueImages(variation?.groups || [], selectedValue);

  return {
    images,
    fabricSample,
    selectedValue,
    form: {
      ...form,
      ctaLabel: isLoading || selectedValue?.variantKey ? t('h24_move_to_pdp') : t('h24_configure'),
      backLabel: t('h24_back_to_result'),
    },
    data: variation,
    isLoading,
    count,
    error,
  };
};
